/** GPU Color Balance — fragment-shader port of
 *  `lib/adjustments/color-balance.ts`.
 *
 *  Direct RGB math (no HSL round-trip). Triangular tonal-band
 *  weights compute per-pixel from luma:
 *
 *    L < 0.5: wShadows = 1 - 2L, wMidtones = 2L, wHighlights = 0
 *    L ≥ 0.5: wShadows = 0, wMidtones = 2 - 2L, wHighlights = 2L - 1
 *
 *  The three weights sum to 1 at every L, bounding the total
 *  shift. SHIFT_SCALE (0.005) matches the JS — chosen so ±100 on
 *  one axis is a visible cast without instant clipping.
 *
 *  Preserve Luminosity branch: rescales post-shift RGB to restore
 *  source luma. Skips when post-shift L ≈ 0 (divide-by-zero
 *  guard). Mirrors the JS conditional. */

import { COLOR_BALANCE_SHIFT_SCALE } from '../../adjustments/color-balance';
import { LUMA_GLSL } from '../glsl-helpers';
import type { ColorBalanceValue } from '../../adjustments/color-balance';
import { applyShader } from '../runtime';

const COLOR_BALANCE_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec3 u_shadows;       // [cyan-red, magenta-green, yellow-blue] in slider units
uniform vec3 u_midtones;
uniform vec3 u_highlights;
uniform float u_preserve;     // 0.0 or 1.0
out vec4 outColor;
${LUMA_GLSL}

// Interpolated from the canonical CPU export so the GPU shader and
// JS dispatch are guaranteed to share the same scale factor. Drift
// here would silently break byte parity between paths.
const float SHIFT_SCALE = ${COLOR_BALANCE_SHIFT_SCALE.toFixed(6)};

void main() {
  vec4 c = texture(u_source, v_uv);
  float L = LUMA_R * c.r + LUMA_G * c.g + LUMA_B * c.b;
  // Triangular weights summing to 1.
  float wS, wM, wH;
  if (L < 0.5) {
    wS = 1.0 - 2.0 * L;
    wM = 2.0 * L;
    wH = 0.0;
  } else {
    wS = 0.0;
    wM = 2.0 - 2.0 * L;
    wH = 2.0 * L - 1.0;
  }
  // Per-channel additive shifts.
  vec3 shifted = c.rgb + (u_shadows * wS + u_midtones * wM + u_highlights * wH) * SHIFT_SCALE;
  // Preserve luminosity rescale (skipped when post-shift L ~ 0).
  if (u_preserve > 0.5) {
    float Lnew = LUMA_R * shifted.r + LUMA_G * shifted.g + LUMA_B * shifted.b;
    if (Lnew > 1e-6) shifted *= L / Lnew;
  }
  shifted = clamp(shifted, 0.0, 1.0);
  vec3 rgb = floor(shifted * 255.0 + 0.5) / 255.0;
  outColor = vec4(rgb, c.a);
}
`;

export function applyColorBalanceOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: ColorBalanceValue,
): boolean {
  return applyShader({
    source,
    target,
    fragShaderSrc: COLOR_BALANCE_FRAG,
    uniforms: {
      u_shadows: { type: 'vec3', value: [value.shadows[0], value.shadows[1], value.shadows[2]] },
      u_midtones: {
        type: 'vec3',
        value: [value.midtones[0], value.midtones[1], value.midtones[2]],
      },
      u_highlights: {
        type: 'vec3',
        value: [value.highlights[0], value.highlights[1], value.highlights[2]],
      },
      u_preserve: { type: 'float', value: value.preserveLuminosity ? 1 : 0 },
    },
  });
}
